<template>
  <div class="top-employees-table">
    <h6 class="top-employees-table__title">Top Employees recognised</h6>
    <b-spinner v-if="loading" small />
    <template v-else-if="employees.length > 0">
      <b-table :items="employees" :fields="fields" hover responsive borderless class="top-employees-table__table">
        <template #cell(name)="{ item }">
          <div class="top-employees-table__name-cell">
            <div class="top-employees-table__avatar">
              <img v-if="item.avatar_url" :src="item.avatar_url" :alt="item.full_name" />
              <font-awesome-icon v-else :icon="['fal', 'user']" />
            </div>
            <span>{{ item.full_name }}</span>
          </div>
        </template>

        <template #cell(email)="{ item }">
          <span class="top-employees-table__email">{{ item.email }}</span>
        </template>

        <template #cell(nominations_count)="{ item }">
          <span>{{ item.nominations_count }}</span>
        </template>

        <template #cell(recognitions_count)="{ item }">
          <span>{{ item.recognitions_count }}</span>
        </template>
      </b-table>
    </template>
    <div v-else class="top-employees-table__empty">
      <p class="text-muted mb-0">No data available.</p>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

defineProps({
  employees: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const fields = computed(() => [
  { key: 'name', label: 'Name', thClass: 'top-employees-table__th' },
  { key: 'email', label: 'Email', thClass: 'top-employees-table__th' },
  {
    key: 'nominations_count',
    label: 'Nominations',
    thClass: 'top-employees-table__th top-employees-table__th--right',
    tdClass: 'text-end',
  },
  {
    key: 'recognitions_count',
    label: 'Recognitions',
    thClass: 'top-employees-table__th top-employees-table__th--right',
    tdClass: 'text-end',
  },
]);
</script>

<style scoped lang="scss">
.top-employees-table {
  background-color: var(--palette-color-base-white);
  border-radius: 4px;
  box-shadow: var(--depth-2);
  padding: 1.25rem 1.5rem;
  height: 100%;

  &__title {
    font-weight: 600;
    margin-bottom: 1rem;
    color: var(--palette-color-default);
  }

  &__table {
    margin-bottom: 0;

    :deep(th) {
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      color: var(--palette-color-default-lighten-30);
      border-bottom: 1px solid var(--palette-color-default-lighten-80);
      padding: 0.5rem 0.75rem;
    }

    :deep(td) {
      font-size: 14px;
      color: var(--palette-color-default);
      padding: 0.75rem;
      vertical-align: middle;
      border-bottom: 1px solid var(--palette-color-default-lighten-90);
    }

    :deep(tr:last-child td) {
      border-bottom: none;
    }
  }

  &__th--right {
    text-align: right;
  }

  &__name-cell {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }

  &__avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background-color: var(--palette-color-default-lighten-80);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    svg {
      color: var(--palette-color-default-lighten-30);
      font-size: 14px;
    }
  }

  &__email {
    color: var(--palette-color-default-lighten-30);
  }

  &__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 100px;
  }
}
</style>
